import React, { useEffect, useState } from 'react';
import { networkGraphAPI } from '../API/NetworkGraphAPI';
import LiqExhibitTitleContainer from 'features/shared/UI/layout/LiqExhibitTitleContainer';
import { Breadcrumbs, Button } from '@mui/material';
import { replace_underscores_capitalize } from 'helpers/string_formatters';
import { Link } from 'react-router-dom';

interface NetworkGraphFrameProps {
    setDisplayNetworkGraph: (x: boolean) => void;
    type: string;
    entityId: string;
    entityName: string;
}

const NetworkGraphFrame = ({
    setDisplayNetworkGraph,
    entityId,
    entityName,
    type,
}: NetworkGraphFrameProps) => {
    const [html, setHTML] = useState<string>('');
    const [error, setError] = useState<boolean>(false);

    useEffect(() => {
        async function fetchNetworkGraphHTML() {
            try {
                const response = await networkGraphAPI.fetchNetworkGraph(entityId, type);
                const backendHtmlString = await response.data;
                setHTML(backendHtmlString);
            } catch (err: any) {
                setHTML('');
                setError(true);
            }
        }
        fetchNetworkGraphHTML();
    }, [entityId, type]);

    return (
        <div className="py-32 lg:px-20 px-4 grid grid-cols-12">
            <div className="col-start-1 col-end-13 flex flex-row justify-between">
                <Breadcrumbs className="col-start-2 lg:col-end-12 col-end-12" separator=">">
                    <Link to="/" className="hover:underline">
                        Lobby<b>IQ</b>
                    </Link>
                    <Link to={`/${type}`} className="hover:underline">
                        {replace_underscores_capitalize(type)}
                    </Link>
                    <div>{entityName}</div>
                </Breadcrumbs>
            </div>
            <div className="mt-4 col-start-1 col-end-13">
                <div className="flex md:flex-row flex-col justify-between gap-2 mb-2">
                    <LiqExhibitTitleContainer
                        title={`Lobbying Network for ${replace_underscores_capitalize(entityName)}`}
                        subtitle=""
                    />
                    <Button
                        onClick={() => setDisplayNetworkGraph(false)}
                        style={{ textTransform: 'none', fontSize: '14px' }}
                        variant="outlined"
                    >
                        Back to Dashboard
                    </Button>
                </div>
                <hr className="h-0.5 my-3 bg-slate-100 rounded" />
                <p className="pt-2 pb-4 w-3/4">
                    {type === 'sectors'
                        ? 'This network diagram represents lobbying communications between organizations within the sector and federal policy-makers for the specified period. Grey nodes are organizations, coloured nodes are individual policy-makers within a government department. Hovering over a node will display a pop-up box with more information.'
                        : 'This network diagram represents lobbying communications between policy-makers within the institution and industry sectors for the specified period. Grey nodes are policy-makers, coloured nodes are sectors. Hovering over a node will display a pop-up box with more information'}
                </p>
                <p className="pt-2 pb-4 w-3/4 text-slate-500 italic">
                    All network diagrams contain up to 90 days worth of meetings or 500 meetings in
                    total, whichever comes first.
                </p>
            </div>
            <div
                className="col-start-1 col-end-13 bg-white shadow-md rounded-md"
                style={{ minHeight: '950px' }}
            >
                {error ? (
                    <div className="flex flex-col items-center justify-center gap-4 h-full">
                        <p>
                            An error occurred while loading the network graph, please try again
                            later.
                        </p>
                        <Button
                            onClick={() => setDisplayNetworkGraph(false)}
                            style={{ textTransform: 'none', fontSize: '14px' }}
                            variant="outlined"
                        >
                            Back to Dashboard
                        </Button>
                    </div>
                ) : (
                    <iframe
                        srcDoc={`
                            <html>
                                <head>
                                <style>
                                    body { display: flex; justify-content: center; }
                                </style>
                                </head>
                                <body>
                                ${html}
                                </body>
                            </html>
                        `}
                        title="Network Graph"
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        className="w-full h-full"
                    />
                )}
            </div>
        </div>
    );
};

export default NetworkGraphFrame;
